import React, { useState } from "react";
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import CreateIcon from '@mui/icons-material/Create';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

export default function FlashCardView({ words, flashIndex, setFlashIndex, setFlashMode, speakWord }) {
    const [flipped, setFlipped] = useState(false);
    const [practiceMode, setPracticeMode] = useState(false);
    const [practiceAttempts, setPracticeAttempts] = useState([]);
    const [currentInput, setCurrentInput] = useState('');
    const practiceInputRef = React.useRef(null);

    const word = words[flashIndex];

    const handleCardClick = () => setFlipped(!flipped);

    const startPracticeTyping = () => {
        setPracticeMode(true);
        setPracticeAttempts([]);
        setCurrentInput('');
        setTimeout(() => practiceInputRef.current?.focus(), 100);
    };

    const handlePracticeSubmit = () => {
        if (!currentInput.trim()) return;
        const correctWord = word.word.toLowerCase();
        const userWord = currentInput.trim().toLowerCase();
        const isCorrect = userWord === correctWord;
        const newAttempts = [...practiceAttempts, { input: currentInput, correct: isCorrect }];
        setPracticeAttempts(newAttempts);
        setCurrentInput('');

        if (newAttempts.length >= 10) {
            setTimeout(() => {
                const correctCount = newAttempts.filter(a => a.correct).length;
                alert(correctCount === 10
                    ? `Perfect! You typed "${word.word}" correctly 10 times!`
                    : `Good effort! You got ${correctCount}/10 correct. Keep practicing!`
                );
                setPracticeMode(false);
                setPracticeAttempts([]);
            }, 100);
        } else {
            practiceInputRef.current?.focus();
        }
    };

    return (
        <div className="fc-view">
            {/* Header */}
            <div className="fc-header">
                <span className="fc-progress">{flashIndex + 1} / {words.length}</span>
                <button className="fc-exit-btn" onClick={() => { setFlashMode(false); setFlashIndex(0); }}>
                    <CloseOutlinedIcon style={{ fontSize: 20 }} />
                </button>
            </div>

            {!practiceMode ? (
                <>
                    {/* Card */}
                    <div
                        className={`fc-card ${flipped ? 'fc-card-back' : 'fc-card-front'}`}
                        onClick={handleCardClick}
                    >
                        {!flipped ? (
                            <div className="fc-card-inner">
                                <p className="fc-card-label">Tap to reveal meaning</p>
                                <div className="fc-word">{word.word}</div>
                                <SyncAltIcon className="fc-flip-icon" />
                            </div>
                        ) : (
                            <div className="fc-card-inner">
                                <p className="fc-card-label">Tap to see word</p>
                                <div className="fc-meaning">{word.meaningEn || 'No meaning'}</div>
                                {word.meaningZh && <div className="fc-meaning-zh">{word.meaningZh}</div>}
                                <SyncAltIcon className="fc-flip-icon" />
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="fc-actions">
                        <button className="fc-btn fc-btn-audio" onClick={() => speakWord(flashIndex)}>
                            <VolumeUpIcon />
                            <span>Play Sound</span>
                        </button>
                        <button className="fc-btn fc-btn-practice" onClick={startPracticeTyping}>
                            <CreateIcon />
                            <span>Practice Typing</span>
                        </button>
                    </div>

                    {/* Navigation */}
                    <div className="fc-nav">
                        <button
                            className="fc-nav-btn"
                            onClick={() => { setFlashIndex(flashIndex - 1); setFlipped(false); }}
                            disabled={flashIndex === 0}
                        >
                            <ArrowBackIcon />
                        </button>
                        <div className="fc-nav-dots">
                            {words.slice(Math.max(0, flashIndex - 2), Math.min(words.length, flashIndex + 3)).map((_, i) => {
                                const idx = Math.max(0, flashIndex - 2) + i;
                                return <span key={idx} className={`fc-dot${idx === flashIndex ? ' active' : ''}`} />;
                            })}
                        </div>
                        {flashIndex + 1 < words.length ? (
                            <button
                                className="fc-nav-btn fc-nav-next"
                                onClick={() => { setFlashIndex(flashIndex + 1); setFlipped(false); }}
                            >
                                <ArrowForwardIcon />
                            </button>
                        ) : (
                            <button
                                className="fc-nav-btn fc-nav-next fc-nav-finish"
                                onClick={() => { setFlashMode(false); setFlashIndex(0); }}
                            >
                                <CheckIcon />
                            </button>
                        )}
                    </div>
                </>
            ) : (
                <div className="fc-practice">
                    <div className="fc-practice-header">
                        <span className="fc-practice-word">{word.word}</span>
                        <div className="fc-practice-prog-label">{practiceAttempts.length} / 10</div>
                    </div>

                    <div className="fc-practice-bar">
                        <div className="fc-practice-bar-fill" style={{ width: `${(practiceAttempts.length / 10) * 100}%` }} />
                    </div>

                    <div className="fc-practice-input-row">
                        <input
                            ref={practiceInputRef}
                            type="text"
                            className="fc-practice-input"
                            value={currentInput}
                            onChange={(e) => setCurrentInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handlePracticeSubmit(); } }}
                            placeholder="Type the word..."
                            autoComplete="off"
                        />
                        <button className="fc-practice-submit" onClick={handlePracticeSubmit} disabled={!currentInput.trim()}>
                            <CheckIcon />
                        </button>
                    </div>

                    <div className="fc-attempts">
                        {practiceAttempts.length === 0 ? (
                            <p className="fc-attempts-empty">No attempts yet — start typing!</p>
                        ) : (
                            <div className="fc-attempts-list">
                                {practiceAttempts.map((a, i) => (
                                    <div key={i} className={`fc-attempt ${a.correct ? 'correct' : 'incorrect'}`}>
                                        <span>{i + 1}. {a.input}</span>
                                        {a.correct ? <CheckIcon /> : <CloseIcon />}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <button
                        className="btn btn-outline"
                        onClick={() => { setPracticeMode(false); setPracticeAttempts([]); setCurrentInput(''); }}
                        style={{ width: '100%', marginTop: 12 }}
                    >
                        <ArrowBackIcon className="icon-sm" /> Back to Card
                    </button>
                </div>
            )}
        </div>
    );
}
